import { API_CONFIG, DEFAULT_CREDENTIALS } from '../../config/api';
import authService from '../auth';

// Helius API Response Types
export interface SolanaBalance {
  jsonrpc: string;
  id: string;
  result: {
    context: {
      slot: number;
      apiVersion?: string;
    };
    value: number; // Balance in lamports (1 SOL = 1,000,000,000 lamports)
  };
}

export interface SolanaNFT {
  id: string;
  name: string;
  image: string;
  description: string;
  symbol: string;
  externalUrl: string;
  collection: string;
  owner: string;
}

export interface SolanaCollection {
  address: string;
  name: string;
  symbol: string;
  description: string;
  image: string;
  externalUrl?: string;
}

export type SolanaNetwork = 'mainnet' | 'devnet';

class HeliusService {
  private authAttempted: boolean = false;
  private balanceCache: Map<string, { balance: SolanaBalance | null; expiry: number }> = new Map();
  private nftsCache: Map<string, { nfts: SolanaNFT[]; expiry: number }> = new Map();
  private collectionsCache: Map<string, { collections: SolanaCollection[]; expiry: number }> = new Map();

  private async ensureAuth(): Promise<void> {
    if (this.authAttempted || authService.isAuthenticated()) return;
    
    this.authAttempted = true;
    
    // Skip authentication if no credentials are provided
    if (!DEFAULT_CREDENTIALS.username || !DEFAULT_CREDENTIALS.password) {
      console.warn('No API credentials provided. Set REACT_APP_API_USERNAME and REACT_APP_API_PASSWORD environment variables.');
      return;
    }
    
    try {
      await authService.login(DEFAULT_CREDENTIALS.username, DEFAULT_CREDENTIALS.password);
    } catch (error) {
      if (error instanceof Error && error.message.includes('429')) {
        console.warn('⏳ Rate limited - too many login attempts. Please wait before trying again.');
      } else if (error instanceof Error && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        console.error('🚨 CORS Error: API server needs to allow requests from the current domain');
        console.error('❌ This is a server-side configuration issue. Contact the API administrator.');
      } else {
        console.error('❌ Authentication failed:', error);
      }
    }
  }

  /**
   * Get SOL balance for a specific Solana wallet address
   * @param address - The Solana wallet address
   * @param network - The Solana network (mainnet or devnet)
   * @param refresh - Whether to bypass cache and refresh data
   * @returns Promise<SolanaBalance | null>
   */
  async getBalance(address: string, network: SolanaNetwork = 'mainnet', refresh: boolean = false): Promise<SolanaBalance | null> {
    const cacheKey = `${address}-${network}`;
    
    // Check cache first if not refreshing
    if (!refresh) {
      const cached = this.balanceCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.balance;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, cannot fetch Solana balance');
        return null;
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '&refresh=true' : '';
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.HELIUS_BALANCE}/${address}?network=${network}${refreshParam}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          console.error('Invalid Solana address provided');
        } else if (response.status === 503) {
          console.error('Helius API not configured or unavailable');
        } else {
          console.error(`Failed to fetch balance: ${response.status}`);
        }
        return null;
      }

      const data: SolanaBalance = await response.json();
      
      // Cache the result for 2 minutes (balance changes frequently)
      this.balanceCache.set(cacheKey, {
        balance: data,
        expiry: Date.now() + 2 * 60 * 1000
      });
      
      return data;

    } catch (error) {
      if (error instanceof Error && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        console.warn('🚨 CORS/Network error: Helius API unavailable. Using fallback behavior.');
      } else {
        console.error('Error fetching Solana balance:', error);
      }
      return null;
    }
  }

  /**
   * Get all NFTs owned by a specific Solana wallet address
   * @param ownerAddress - The Solana wallet address
   * @param network - The Solana network (mainnet or devnet)
   * @param refresh - Whether to bypass cache and refresh data
   * @returns Promise<SolanaNFT[]>
   */
  async getNFTsByOwner(ownerAddress: string, network: SolanaNetwork = 'mainnet', refresh: boolean = false): Promise<SolanaNFT[]> {
    const cacheKey = `${ownerAddress}-${network}`;
    
    // Check cache first if not refreshing
    if (!refresh) {
      const cached = this.nftsCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.nfts;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, cannot fetch Solana NFTs');
        return [];
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '&refresh=true' : '';
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.HELIUS_NFTS}/${ownerAddress}/nfts?network=${network}${refreshParam}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          console.error('Invalid Solana address provided');
        } else if (response.status === 503) {
          console.error('Helius API not configured or unavailable');
        } else {
          console.error(`Failed to fetch NFTs: ${response.status}`);
        }
        return [];
      }

      const data: SolanaNFT[] = await response.json();
      
      // Cache the result for 5 minutes (NFTs change less frequently)
      this.nftsCache.set(cacheKey, {
        nfts: data,
        expiry: Date.now() + 5 * 60 * 1000
      });
      
      return data;

    } catch (error) {
      if (error instanceof Error && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        console.warn('🚨 CORS/Network error: Helius API unavailable. Using fallback behavior.');
      } else {
        console.error('Error fetching Solana NFTs:', error);
      }
      return [];
    }
  }

  /**
   * Get all NFT collections owned by a specific Solana wallet address
   * @param ownerAddress - The Solana wallet address
   * @param network - The Solana network (mainnet or devnet)
   * @param refresh - Whether to bypass cache and refresh data
   * @returns Promise<SolanaCollection[]>
   */
  async getCollectionsByOwner(ownerAddress: string, network: SolanaNetwork = 'mainnet', refresh: boolean = false): Promise<SolanaCollection[]> {
    const cacheKey = `${ownerAddress}-${network}`;
    
    // Check cache first if not refreshing
    if (!refresh) {
      const cached = this.collectionsCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.collections;
      }
    }

    try {
      await this.ensureAuth();
      
      if (!authService.isAuthenticated()) {
        console.warn('Authentication failed, cannot fetch Solana collections');
        return [];
      }

      const token = await authService.getValidToken();
      const refreshParam = refresh ? '&refresh=true' : '';
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.HELIUS_COLLECTIONS}/${ownerAddress}/collections?network=${network}${refreshParam}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          console.error('Invalid Solana address provided');
        } else if (response.status === 503) {
          console.error('Helius API not configured or unavailable');
        } else {
          console.error(`Failed to fetch collections: ${response.status}`);
        }
        return [];
      }

      const data: SolanaCollection[] = await response.json();
      
      // Cache the result for 10 minutes (collections change infrequently)
      this.collectionsCache.set(cacheKey, {
        collections: data,
        expiry: Date.now() + 10 * 60 * 1000
      });
      
      return data;

    } catch (error) {
      if (error instanceof Error && (error.message.includes('CORS') || error.message.includes('Failed to fetch'))) {
        console.warn('🚨 CORS/Network error: Helius API unavailable. Using fallback behavior.');
      } else {
        console.error('Error fetching Solana collections:', error);
      }
      return [];
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.balanceCache.clear();
    this.nftsCache.clear();
    this.collectionsCache.clear();
  }

  /**
   * Clear cache for specific address and network
   * @param address - The wallet address
   * @param network - The network (optional)
   */
  clearAddressCache(address: string, network?: SolanaNetwork): void {
    const baseKey = network ? `${address}-${network}` : address;
    
    // Clear all caches for this address/network combination
    this.balanceCache.delete(baseKey);
    this.nftsCache.delete(baseKey);
    this.collectionsCache.delete(baseKey);
    
    // If no network specified, clear for both networks
    if (!network) {
      this.balanceCache.delete(`${address}-mainnet`);
      this.balanceCache.delete(`${address}-devnet`);
      this.nftsCache.delete(`${address}-mainnet`);
      this.nftsCache.delete(`${address}-devnet`);
      this.collectionsCache.delete(`${address}-mainnet`);
      this.collectionsCache.delete(`${address}-devnet`);
    }
  }

  /**
   * Convert lamports to SOL
   * @param lamports - Balance in lamports
   * @returns Balance in SOL
   */
  lamportsToSol(lamports: number): number {
    return lamports / 1_000_000_000;
  }

  /**
   * Convert SOL to lamports
   * @param sol - Balance in SOL
   * @returns Balance in lamports
   */
  solToLamports(sol: number): number {
    return sol * 1_000_000_000;
  }

  /**
   * Format SOL balance for display
   * @param lamports - Balance in lamports
   * @param decimals - Number of decimal places (default: 4)
   * @returns Formatted SOL string
   */
  formatSolBalance(lamports: number, decimals: number = 4): string {
    const sol = this.lamportsToSol(lamports);
    return `${sol.toFixed(decimals)} SOL`;
  }

  /**
   * Validate Solana address format
   * @param address - The address to validate
   * @returns boolean indicating if address is valid format
   */
  isValidSolanaAddress(address: string): boolean {
    // Basic validation: Solana addresses are base58 encoded and typically 32-44 characters
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
    return base58Regex.test(address);
  }

  /**
   * Check if a Solana network string is valid
   * @param network - The network to validate
   * @returns boolean indicating if network is valid
   */
  isValidNetwork(network: string): network is SolanaNetwork {
    return network === 'mainnet' || network === 'devnet';
  }
}

const heliusService = new HeliusService();
export default heliusService;
